import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Heart, UserPlus, IdentificationCard, Eye, EyeSlash } from '@phosphor-icons/react';

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    cui: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!acceptedTerms) {
      setError('Trebuie să acceptați Termenii și Condițiile, GDPR și Politica de Securitate');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Parolele nu coincid');
      return;
    }

    if (formData.password.length < 6) {
      setError('Parola trebuie să aibă cel puțin 6 caractere');
      return;
    }

    setIsLoading(true);

    const result = await register(
      formData.email,
      formData.password,
      formData.name,
      formData.cui
    );
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left side - Gradient */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-primary via-primary/90 to-secondary"
      >
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.05%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-30" />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <div className="flex items-center gap-3 mb-6">
            <Heart weight="fill" className="w-10 h-10" />
            <span className="text-3xl font-bold font-[Manrope]">MEDICULDIGITAL</span>
          </div>
          <h1 className="text-4xl font-bold font-[Manrope] mb-4">
            Creează-ți Contul
          </h1>
          <p className="text-lg opacity-90 max-w-md">
            Alătură-te platformei MEDICULDIGITAL pentru consultații medicale digitale asistate, bazate pe tehnologia S8NTH Strategic Engine.
          </p>
          <ul className="mt-6 space-y-3 opacity-90">
            <li className="flex items-center gap-2">
              <div className="w-2 h-2 bg-white rounded-full" />
              Consultații AI disponibile 24/7
            </li>
            <li className="flex items-center gap-2">
              <div className="w-2 h-2 bg-white rounded-full" />
              Istoric medical securizat
            </li>
            <li className="flex items-center gap-2">
              <div className="w-2 h-2 bg-white rounded-full" />
              Analiză documente medicale
            </li>
          </ul>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-2 text-primary">
            <Heart weight="fill" className="w-8 h-8" />
            <span className="text-2xl font-bold font-[Manrope]">MEDICULDIGITAL</span>
          </div>

          <div className="text-center lg:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold font-[Manrope] text-foreground">
              Înregistrare
            </h2>
            <p className="mt-2 text-muted-foreground">
              Creează un cont pentru a începe consultațiile
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            {error && (
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm" data-testid="register-error">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">Nume complet</Label>
              <Input
                id="name"
                name="name"
                type="text"
                placeholder="Ion Popescu"
                value={formData.name}
                onChange={handleChange}
                required
                data-testid="register-name-input"
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="exemplu@email.com"
                value={formData.email}
                onChange={handleChange}
                required
                data-testid="register-email-input"
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cui" className="flex items-center gap-2">
                <IdentificationCard className="w-4 h-4" />
                Cod Unic de Identificare (CUI)
              </Label>
              <Input
                id="cui"
                name="cui"
                type="text"
                placeholder="ex: 1234567890123"
                value={formData.cui}
                onChange={handleChange}
                required
                data-testid="register-cui-input"
                className="h-11"
              />
              <p className="text-xs text-muted-foreground">
                Codul tău unic pentru identificare în sistemul medical
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password">Parolă</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    data-testid="register-password-input"
                    className="h-11 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? (
                      <EyeSlash weight="bold" className="w-4 h-4" />
                    ) : (
                      <Eye weight="bold" className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmă</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    data-testid="register-confirm-password-input"
                    className="h-11 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="toggle-confirm-password-visibility"
                  >
                    {showConfirmPassword ? (
                      <EyeSlash weight="bold" className="w-4 h-4" />
                    ) : (
                      <Eye weight="bold" className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Terms Checkbox */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="acceptTerms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                data-testid="accept-terms-checkbox"
              />
              <label htmlFor="acceptTerms" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
                Accept{' '}
                <Link to="/terms" target="_blank" className="text-primary hover:underline font-medium">
                  Termenii și Condițiile
                </Link>
                ,{' '}
                <Link to="/gdpr" target="_blank" className="text-primary hover:underline font-medium">
                  GDPR
                </Link>
                {' '}și{' '}
                <Link to="/security-policy" target="_blank" className="text-primary hover:underline font-medium">
                  Politica de Securitate
                </Link>
              </label>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 text-base font-semibold"
              disabled={isLoading || !acceptedTerms}
              data-testid="register-submit-button"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  Se creează contul...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <UserPlus weight="bold" className="w-5 h-5" />
                  Creează cont
                </span>
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            Ai deja cont?{' '}
            <Link 
              to="/login" 
              className="text-primary font-semibold hover:underline"
              data-testid="login-link"
            >
              Conectează-te aici
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
